import {
  HHAlert,
  HHBox,
  HHStack,
  HHTable,
  HHTableBody,
  HHTableCell,
  HHTableContainer,
  HHTableHead,
  HHTableRow,
  HHTypography,
} from '@hinge-health/react-component-library';
import { Skeleton, Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { ReactNode } from 'react';
import { SurfaceCard } from './surface-card';

const SKELETON_ROW_COUNT = 5;

export interface TableCardNoun {
  singular: string;
  plural: string;
}

export interface TableCardTestIds {
  table: string;
  skeleton: string;
  empty: string;
  error: string;
  footer: string;
  toolbar: string;
}

/** Every table card derives its test ids from one prefix, the same way. */
export const tableCardTestIds = (prefix: string): TableCardTestIds => ({
  table: prefix,
  skeleton: `${prefix}-skeleton`,
  empty: `${prefix}-empty`,
  error: `${prefix}-error`,
  footer: `${prefix}-footer`,
  toolbar: `${prefix}-toolbar`,
});

export interface TableCardProps {
  testIdPrefix: string;
  /** Accessible name for the `<table>`. */
  ariaLabel: string;
  headers: string[];
  /** Rows currently rendered; drives the footer count and the empty state. */
  count: number;
  noun: TableCardNoun;
  /** Shown when there are no rows; should tell the operator what to do next. */
  emptyMessage: string;
  errorMessage: string;
  /** True while a fetch is in flight. Only blanks the table on first load. */
  isLoading?: boolean;
  /** True when the last fetch failed. Rows already loaded stay visible. */
  hasError?: boolean;
  /** Filter controls rendered in a row above the column headers. */
  toolbar?: ReactNode;
  /** Rendered on the right of the footer, next to the "Showing N" count. */
  footer?: ReactNode;
  /** Table body rows. */
  children: ReactNode;
}

const headerCellSx = (theme: Theme): SystemStyleObject<Theme> => ({
  color: theme.palette.text.secondary,
  fontSize: 13,
  fontWeight: 500,
  whiteSpace: 'nowrap',
});

const dividerAbove = (theme: Theme): SystemStyleObject<Theme> => ({
  borderTop: `1px solid ${theme.palette.divider}`,
});

const dividerBelow = (theme: Theme): SystemStyleObject<Theme> => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
});

const SkeletonRows = ({
  columns,
  testId,
}: {
  columns: number;
  testId: string;
}): JSX.Element => (
  <>
    {Array.from({ length: SKELETON_ROW_COUNT }, (_, rowIndex) => (
      <HHTableRow
        key={rowIndex}
        data-testid={rowIndex === 0 ? testId : undefined}
      >
        {Array.from({ length: columns }, (__, cellIndex) => (
          <HHTableCell key={cellIndex}>
            <Skeleton variant="text" />
          </HHTableCell>
        ))}
      </HHTableRow>
    ))}
  </>
);

const showingLabel = (count: number, noun: TableCardNoun): string =>
  `Showing ${count} ${count === 1 ? noun.singular : noun.plural}`;

/**
 * The module's one table surface: card, optional toolbar, column headers,
 * skeleton rows on first load, an alert for "nothing here" that says what to
 * do next, an alert for "the fetch failed" that leaves loaded rows in place,
 * and a footer with the row count and a pager slot.
 */
export const TableCard = ({
  testIdPrefix,
  ariaLabel,
  headers,
  count,
  noun,
  emptyMessage,
  errorMessage,
  isLoading = false,
  hasError = false,
  toolbar,
  footer,
  children,
}: TableCardProps): JSX.Element => {
  const ids = tableCardTestIds(testIdPrefix);
  const isFirstLoad = isLoading && count === 0;
  // A failed fetch owns the empty space; showing "nothing matches" under an
  // error would read as a result rather than a failure.
  const isEmpty = !isLoading && !hasError && count === 0;
  const hasRowsToShow = count > 0 || isFirstLoad;

  return (
    <SurfaceCard sx={{ overflow: 'hidden' }}>
      {toolbar && (
        <HHBox
          paddingX={5}
          paddingY={3}
          data-testid={ids.toolbar}
          sx={dividerBelow}
        >
          {toolbar}
        </HHBox>
      )}

      {hasError && (
        <HHBox
          padding={4}
          data-testid={ids.error}
          sx={hasRowsToShow ? dividerBelow : undefined}
        >
          <HHAlert hhVariant="outlined" severity="error">
            {errorMessage}
          </HHAlert>
        </HHBox>
      )}

      {isEmpty && (
        <HHBox padding={6} data-testid={ids.empty}>
          <HHAlert hhVariant="outlined" severity="info">
            {emptyMessage}
          </HHAlert>
        </HHBox>
      )}

      {hasRowsToShow && (
        <HHTableContainer data-testid={isFirstLoad ? undefined : ids.table}>
          <HHTable aria-label={ariaLabel} aria-busy={isLoading}>
            <HHTableHead>
              <HHTableRow>
                {headers.map(header => (
                  <HHTableCell key={header} sx={headerCellSx}>
                    {header}
                  </HHTableCell>
                ))}
              </HHTableRow>
            </HHTableHead>
            <HHTableBody>
              {isFirstLoad ? (
                <SkeletonRows columns={headers.length} testId={ids.skeleton} />
              ) : (
                children
              )}
            </HHTableBody>
          </HHTable>
        </HHTableContainer>
      )}

      <HHStack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        paddingX={5}
        paddingY={3}
        data-testid={ids.footer}
        sx={dividerAbove}
      >
        {/* WCAG 4.1.3: the loading-to-loaded flip is a status message, so it
            is announced politely without the operator having to focus it. */}
        <HHTypography
          hhVariant="variant-bypass"
          variant="body2"
          color="text.secondary"
          role="status"
          aria-live="polite"
        >
          {isFirstLoad ? `Loading ${noun.plural}…` : showingLabel(count, noun)}
        </HHTypography>
        {footer}
      </HHStack>
    </SurfaceCard>
  );
};
